import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import {
  SingleMonomerQuery,
  SingleMonomerQueryVariables,
} from 'molbio/monomers/__generated/SingleMonomerQuery';
import MonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from 'molbio/monomers/admin/MonomerConfigurationPageContainer';
import SharedMonomerManualEditPage from 'molbio/monomers/admin/SharedMonomerManualEditPage';
import {gqlToAPINaturalAnalogue} from 'molbio/monomers/MonomersUtil';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';

import {SINGLE_MONOMER_QUERY} from '../MonomerConstants';
import {MonomerUpdate, updateMonomer as updateMonomerHelper, validateMonomerInput} from '../MonomersDAO';

export interface MonomerManualEditPageProps {
  monomerId: string;
}

export function MonomerManualEditConfirmationModal(props: {
  onRequestClose: () => void;
  updateMonomer: () => void;
}): JSX.Element {
  return (
    <Modal
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          onClick: () => {
            props.updateMonomer();
            props.onRequestClose();
          },
          text: 'Yes, save monomer',
        },
      ]}
      title="Edit monomer structure?"
    >
      <p>
        If this monomer is used in a complex polymer, editing the structure may invalidate registered complex
        polymers and remove existing conjugate attachments. Are you sure you want to continue editing the
        structure?
      </p>
    </Modal>
  );
}

export default function MonomerManualEditPage({monomerId}: MonomerManualEditPageProps): JSX.Element {
  return (
    <BenchlingQuery<SingleMonomerQuery, SingleMonomerQueryVariables>
      query={SINGLE_MONOMER_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      renderCachedDataWhenRefetching={true}
      variables={{id: monomerId}}
    >
      {({monomer}) => {
        if (monomer.__typename === 'MonomerNode') {
          if (!monomer.isNatural) {
            return (
              <SharedMonomerManualEditPage
                monomerId={monomerId}
                initialName={monomer.name}
                initialCustomMolecularWeight={monomer.customMolecularWeight}
                initialSmilesInput={monomer.originalSmiles}
                initialSymbol={monomer.symbol}
                naturalAnalogueForValidation={gqlToAPINaturalAnalogue(monomer.naturalAnalogue)}
                validate={validateMonomerInput}
                onSave={async (update: MonomerUpdate) => {
                  await updateMonomerHelper(monomerId, update);
                }}
                onSaveRequested={({didStructureChange, save}) => {
                  if (didStructureChange && BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES) {
                    legacyRunModal(MonomerManualEditConfirmationModal, {updateMonomer: save});
                  } else {
                    save();
                  }
                }}
                libraryUrl="/monomers"
                pageContainer={
                  <MonomerConfigurationPageContainer purpose={MONOMER_CONFIGURATION_PURPOSE.EDIT} />
                }
                dataTestComponent="MonomerManualEditPage"
              />
            );
          } else {
            notifications.info('Cannot edit a natural monomer');
          }
        }
        navigateToURL('/monomers');
        return null;
      }}
    </BenchlingQuery>
  );
}
